import React from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  styled,
  Badge,
  alpha,
  Stack,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Chat as ChatIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon,
  Feedback as FeedbackIcon,
} from "@mui/icons-material";
import { BackButton, BackDropLoading } from "@/organism";
import SessionFeedbackModal from "@/organism/SessionFeedbackModal";
import { useSessionDetail } from "./useSessionDetail";
import { SessionInfoTab } from "./SessionInfoTab";
import { UserInfoTab } from "./UserInfoTab";
import { ChatMessagesTab } from "./ChatMessagesTab";
import { StatisticsTab } from "./StatisticsTab";
import { FeedbackTab } from "./FeedbackTab";

// 스타일링된 탭 컴포넌트
const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 64,
  fontSize: "0.9rem",
  fontWeight: 500,
  textTransform: "none",
  transition: "all 0.2s ease-in-out",
  "&.Mui-selected": {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&.MuiTab-root": {
    padding: "12px 24px",
  },
  "& .MuiTab-iconWrapper": {
    marginRight: 8,
    marginBottom: "0 !important",
  },
}));

// 탭 컨테이너
const TabContainer = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  position: "relative",
  zIndex: 1,
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

/**
 * 채팅 세션 상세 페이지
 *
 * @description 특정 채팅 세션의 상세 정보와 메시지를 표시하는 페이지
 * @author Max <sud665@ymjlab.com>
 * @version 2.0.0
 * @since 2025-01-16
 */
export default function SuperChatLogDetail() {
  const {
    sessionId,
    currentSession,
    messages,
    loading,
    activeTab,
    feedbackModalOpen,
    sessionFeedback,
    feedbackLoading,
    handleBack,
    handleTabChange,
    handleOpenFeedbackModal,
    handleCloseFeedbackModal,
    handleFeedbackUpdated,
  } = useSessionDetail();

  // 로딩 상태
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ px: 2 }}>
        <BackDropLoading isLoading={loading} />
      </Container>
    );
  }

  // 세션이 없는 경우
  if (!currentSession) {
    return (
      <Container maxWidth="xl" sx={{ px: 2, py: 4 }}>
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            세션을 찾을 수 없습니다.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            요청하신 세션 ID: {sessionId}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ borderRadius: 2 }}
          >
            목록으로 돌아가기
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ px: 2 }}>
      {/* 페이지 헤더 */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="column" spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <BackButton to="/chat/log">채팅 로그 목록</BackButton>

            <Button
              variant="contained"
              startIcon={<FeedbackIcon />}
              onClick={handleOpenFeedbackModal}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Feedback
            </Button>
          </Stack>

          <Box>
            <Typography
              component="h1"
              variant="h4"
              fontWeight="bold"
              color="text.primary"
              gutterBottom
            >
              채팅 세션 상세 정보
            </Typography>

            {currentSession.sessionTitle && (
              <Typography
                variant="h6"
                color="primary"
                fontWeight="600"
                sx={{ mb: 1 }}
              >
                {currentSession.sessionTitle}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary">
              세션 ID: {currentSession.sessionId} •
              {currentSession.user?.userName || "게스트"} •
              {new Date(currentSession.startedAt).toLocaleDateString("ko-KR")}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* 탭 네비게이션 */}
      <TabContainer>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="fullWidth"
          sx={{
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <StyledTab
            label={
              <Badge
                color="primary"
                badgeContent={messages.length}
                invisible={messages.length === 0}
                sx={{ "& .MuiBadge-badge": { right: -14, top: 4 } }}
              >
                채팅 메시지
              </Badge>
            }
            icon={<ChatIcon />}
            iconPosition="start"
          />
          <StyledTab
            label="사용자 정보"
            icon={<PersonIcon />}
            iconPosition="start"
          />
          <StyledTab
            label="세션 정보"
            icon={<InfoIcon />}
            iconPosition="start"
          />
          <StyledTab
            label="통계 정보"
            icon={<AnalyticsIcon />}
            iconPosition="start"
          />
          <StyledTab
            label={
              <Badge
                color="secondary"
                variant="dot"
                invisible={!sessionFeedback}
                sx={{ "& .MuiBadge-badge": { right: -6, top: 6 } }}
              >
                피드백
              </Badge>
            }
            icon={<FeedbackIcon />}
            iconPosition="start"
          />
        </Tabs>
      </TabContainer>

      {/* 탭 컨텐츠 */}
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 3,
          backgroundColor: "background.paper",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
        }}
      >
        {activeTab === 0 && <ChatMessagesTab messages={messages} />}
        {activeTab === 1 && <UserInfoTab session={currentSession} />}
        {activeTab === 2 && <SessionInfoTab session={currentSession} />}
        {activeTab === 3 && <StatisticsTab session={currentSession} />}
        {activeTab === 4 && (
          <FeedbackTab
            session={currentSession}
            feedback={sessionFeedback}
            loading={feedbackLoading}
          />
        )}
      </Box>

      {/* 피드백 모달 */}
      <SessionFeedbackModal
        open={feedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        sessionId={parseInt(sessionId || "0")}
        sessionTitle={currentSession.sessionTitle}
        onFeedbackUpdated={handleFeedbackUpdated}
      />
    </Container>
  );
}
